import axios from "../AxiosInterCeptor/AxiosInterCeptor";

/**
 * RoadZen Users page — the backend's /roadzen routes (the backend talks to RoadZen with its key; this file never calls
 * RoadZen itself). Never throws: the result is the server's envelope { error, message, data, code? }, or
 * { error: true, message } on an HTTP error, or { error: true, noResponse: true, message } when nothing answered
 * (same as Service/BriskV2Service.js).
 */
const URL = process.env.React_App_BaseUrl;
const BASE = () => `${URL}/roadzen`;

const NO_RESPONSE = "No answer from the server. Please check your connection and try again";

const settle = (request) =>
  request
    .then((response) => {
      const body = response && response.data;
      return body && typeof body === "object" ? body : { error: true, message: "Unexpected answer from the server" };
    })
    .catch((error) => {
      if (error && error.response) {
        const body = error.response.data || {};
        return { error: true, message: body.message || `The server answered ${error.response.status}`, code: body.code };
      }
      return { error: true, noResponse: true, message: NO_RESPONSE };
    });

/** RoadZen's users (every page), who is active, and whether this login may change them (`can.manage`). */
export const listRoadzenUsers = () => settle(axios.get(`${BASE()}/users`));
/** Our own history of changes, newest first. */
export const listRoadzenHistory = (params) => settle(axios.get(`${BASE()}/history`, { params }));
/** Super admin only: { name, mobile, email?, address?, city?, state? }. */
export const createRoadzenUser = (body) => settle(axios.post(`${BASE()}/users`, body));
/** Super admin only: activate (switches every other user off first) or deactivate. */
export const setRoadzenUserStatus = (id, active) => settle(axios.post(`${BASE()}/users/${encodeURIComponent(id)}/status`, { active }));

/* ---------------------------------------------------------------- Zen Wallet (the agency's Company Wallet "RoadZen") */

/** The admin: his agency's balance / minimum / ledger check. The super admin: every agency (`rows`) and the `total`. */
export const getRoadzenCompanyWalletSummary = () => settle(axios.get(`${BASE()}/company-wallet/summary`));
/** The ledger newest first: `params` = page, limit (and clientId for the super admin). */
export const getRoadzenCompanyWalletLedger = (params) => settle(axios.get(`${BASE()}/company-wallet/ledger`, { params }));

/* ---------------------------------------------------------------- Wallet page (RoadZen's wallet of one user) */

/** Balance, Total Added, Total Used, whether they add up, and `can.add` (this user is the active one) / `can.testCredit`. */
export const getRoadzenWalletSummary = (userId) => settle(axios.get(`${BASE()}/users/${encodeURIComponent(userId)}/wallet/summary`));
/** One statement page, newest first: { rows, total }. */
export const getRoadzenWalletStatement = (userId, params) =>
  settle(axios.get(`${BASE()}/users/${encodeURIComponent(userId)}/wallet/statement`, { params }));
/** The user's payment links, newest first: { rows, total }. */
export const listRoadzenTopups = (userId, params) => settle(axios.get(`${BASE()}/users/${encodeURIComponent(userId)}/wallet/topups`, { params }));
/** RoadZen's current word on one payment link. */
export const checkRoadzenTopup = (userId, topupId) =>
  settle(axios.get(`${BASE()}/users/${encodeURIComponent(userId)}/wallet/topups/${encodeURIComponent(topupId)}`));
/** Add Amount to the ACTIVE user: { mode: "TEST_CREDIT" | "PAYMENT_LINK", amount, note?, requestId } (same requestId = one credit). */
export const addRoadzenWalletMoney = (userId, body) => settle(axios.post(`${BASE()}/users/${encodeURIComponent(userId)}/wallet/add`, body));

/* ---------------------------------------------------------------- certificates (Create Certificate, Certificate List) */

const CERT = () => `${BASE()}/certificates`;
/** Products, the active RoadZen user, the Company Wallet "RoadZen" balance, what this login may do. */
export const getRoadzenCertConfig = () => settle(axios.get(`${CERT()}/config`));
/** Online Policies with a saved policy PDF and no RoadZen certificate yet ("Already created policy"). */
export const listRoadzenCertPolicies = () => settle(axios.get(`${CERT()}/policies`));
export const getRoadzenCertPolicy = (policyId) => settle(axios.get(`${CERT()}/policies/${encodeURIComponent(policyId)}`));
/** FormData: mode, requestId, productId, policyPrice, riskStartDate, form (JSON), onlinePolicyId?, draftId?, policy_copy (PDFs). */
export const createRoadzenCertificate = (formData) => settle(axios.post(CERT(), formData));
export const saveRoadzenCertDraft = (formData) => settle(axios.post(`${CERT()}/drafts`, formData));
export const listRoadzenCertificates = (params) => settle(axios.get(CERT(), { params }));
export const getRoadzenCertificate = (id) => settle(axios.get(`${CERT()}/${encodeURIComponent(id)}`));
/** A link to OUR saved copy: the certificate, or { kind: "insurer", index } for an uploaded policy PDF. */
export const getRoadzenCertDownloadUrl = (id, params) => settle(axios.get(`${CERT()}/${encodeURIComponent(id)}/download`, { params }));
/** Edit = RoadZen's endorsement: { form, reason } (the server sends only what changed). */
export const updateRoadzenCertificate = (id, body) => settle(axios.put(`${CERT()}/${encodeURIComponent(id)}`, body));
export const recheckRoadzenCertificate = (id) => settle(axios.post(`${CERT()}/${encodeURIComponent(id)}/recheck`));
export const cancelRoadzenCertificate = (id, reason) => settle(axios.post(`${CERT()}/${encodeURIComponent(id)}/cancel`, { reason }));
